import { useState, useEffect, useCallback } from "react";

interface UseBannerRotationProps {
  itemCount: number;
  interval?: number; // milliseconds, default 5000
}

interface UseBannerRotationReturn {
  currentIndex: number;
  isTransitioning: boolean;
  isPaused: boolean;
  pause: () => void;
  resume: () => void;
}

export function useBannerRotation({
  itemCount,
  interval = 5000,
}: UseBannerRotationProps): UseBannerRotationReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  useEffect(() => {
    // Don't rotate if only one item or paused
    if (itemCount <= 1 || isPaused) {
      return;
    }

    const timer = setInterval(() => {
      setIsTransitioning(true);

      // Small delay to allow transition animation to start
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % itemCount);
        // Reset transitioning after animation completes
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300); // Match CSS transition duration
      }, 150);
    }, interval);

    return () => clearInterval(timer);
  }, [itemCount, interval, isPaused]);

  return {
    currentIndex,
    isTransitioning,
    isPaused,
    pause,
    resume,
  };
}
